import type { Screen } from '@hub/shared';
import type { IconName } from '../ui.js';
import { CHANNEL_LABEL, REASON_LABEL, SENDER_LABEL, label } from '../../lib/labels.js';

/** Datový model schématu toku: uzly, hrany, výpočty čísel a pravidla z katalogu. */

export type Kind = 'all' | 'service' | 'marketing';
export type CommTab = 'catalogue' | 'flow' | 'map' | 'channels' | 'editor' | 'templates' | 'plan';

/* ------------------------------------------------------------- odpověď --- */

export interface Reaction {
  sent: number;
  dryRun: number;
  delivered: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
  bounced: number;
  lastAt: string | null;
  endpoint: string;
}

export interface CodeStat {
  code: string;
  name: string;
  channel: 'service' | 'marketing';
  sender: 'hub' | 'eshop';
  pending: number;
  held: number;
  deferred: number;
  suppressed: number;
  merged: number;
  expired: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
  bounced: number;
  reasons: Array<{ outcome: string; reason: string; n: number }>;
}

export interface FlowData {
  days: number;
  generatedAt: string;
  settings: {
    sendMode: string;
    marketingDelivery: string;
    windows: {
      standard: { fromHour: number; toHour: number };
      critical: { fromHour: number; toHour: number };
    };
    frequencyCapDays: number;
    mergeWindowHours: number;
    raceWindow: { before_days: number; after_days: number };
    supportPauseDays: number;
    holdoutPercent: number;
  };
  ingest: {
    received: number;
    applied: number;
    stale: number;
    failed: number;
    pending: number;
    duplicatesTracked: boolean;
    bySource: Array<{ source: string; type: string; n: number }>;
    webEvents: number;
    lastAt: string | null;
    webLastAt: string | null;
  };
  contacts: { total: number; withConsent: number; lastAt: string | null };
  journeys: { tripsActive: number; tripsCreated: number; races: number; lastAt: string | null };
  planner: { created: number; open: number; held: number; deferred: number; lastAt: string | null };
  decisions: {
    total: number;
    outcomes: Record<string, number>;
    topReasons: Array<{ outcome: string; reason: string; n: number }>;
    lastAt: string | null;
  };
  templates: { total: number; published: number; synced: number; lastAt: string | null };
  delivery: { service: Reaction; marketing: Reaction };
  reactions: {
    providerEvents: number;
    lastAt: string | null;
    poll: { lastRunAt: string | null; status: string | null; failureStreak: number };
  };
  byCode: CodeStat[];
}

/** Pravidlo z publikovaného katalogu (/api/catalogue → published.rules). */
export interface Rule {
  code: string;
  name: string;
  channel: 'marketing' | 'service';
  sender: 'hub' | 'eshop';
  scope?: string;
  recipient?: string;
  anchor?: string | string[];
  offset?: string | number;
  valid_for?: string;
  requires_date?: string;
  min_lead_days?: number;
  conditions?: string[];
  occurrence?: string;
  merge_group?: string;
  allowed_on_closed?: boolean;
  window?: string;
  pacing_exempt?: boolean;
  priority?: number;
  template_key?: string;
  catch_up?: boolean;
}

export interface JourneyStep {
  code: string;
  name: string;
  channel: 'marketing' | 'service';
  sender: 'hub' | 'eshop';
  included: boolean;
  reason: string | null;
  explanation: string;
}

/* --------------------------------------------------------------- graf --- */

export interface NodeDef {
  id: string;
  lane: number;
  cy: number;
  title: [string, string?];
  sub: string;
  icon: IconName;
  kind: Kind;
  soon?: boolean;
}

export interface EdgeDef {
  id: string;
  from: string;
  to: string;
  kind: Kind;
  soon?: boolean;
  /** Zpětná vazba — kreslí se spodem zpět do příjmu. */
  back?: boolean;
  label?: string;
}

export const LANES = [
  'Zdroje',
  'Hub: příjem',
  'Cesty a kalendář',
  'Plánovač',
  'Rozhodnutí',
  'Šablona',
  'Odeslání',
  'SmartEmailing a reakce',
];

export const NODE_W = 120;
export const NODE_H = 100;
export const PITCH = 150;
export const MARGIN = 24;
export const VIEW_W = MARGIN * 2 + PITCH * 7 + NODE_W;
export const VIEW_H = 570;

export const NODES: NodeDef[] = [
  {
    id: 'src_eshop',
    lane: 0,
    cy: 90,
    title: ['E-shop', 'TicketsGP / LO'],
    sub: 'objednávky a platby',
    icon: 'upload',
    kind: 'all',
  },
  {
    id: 'src_web',
    lane: 0,
    cy: 210,
    title: ['Web snippet', 'události'],
    sub: 'chování na webu',
    icon: 'chart',
    kind: 'all',
  },
  {
    id: 'src_feed',
    lane: 0,
    cy: 330,
    title: ['Feed závodů', ''],
    sub: 'termíny a prodej',
    icon: 'flag',
    kind: 'all',
  },
  {
    id: 'src_se',
    lane: 0,
    cy: 450,
    title: ['SmartEmailing', 'reakce'],
    sub: 'webhook',
    icon: 'refresh',
    kind: 'all',
  },
  {
    id: 'ingest',
    lane: 1,
    cy: 270,
    title: ['Příjem', 'událostí'],
    sub: 'podpis · event_id',
    icon: 'inbox',
    kind: 'all',
  },
  {
    id: 'journeys',
    lane: 2,
    cy: 270,
    title: ['Cesty', 'zákazníků'],
    sub: 'závody · termíny',
    icon: 'route',
    kind: 'all',
  },
  {
    id: 'planner',
    lane: 3,
    cy: 270,
    title: ['Plánovač', ''],
    sub: 'kandidáti z katalogu',
    icon: 'calendar',
    kind: 'all',
  },
  {
    id: 'decisions',
    lane: 4,
    cy: 270,
    title: ['Rozhodnutí', ''],
    sub: 'souhlas · okno · limity',
    icon: 'check',
    kind: 'all',
  },
  {
    id: 'tpl_service',
    lane: 5,
    cy: 190,
    title: ['Šablona', 'servis'],
    sub: 'blocks → plné HTML',
    icon: 'file',
    kind: 'service',
  },
  {
    id: 'tpl_marketing',
    lane: 5,
    cy: 350,
    title: ['Šablona', 'marketing'],
    sub: 'nahraná do SE',
    icon: 'file',
    kind: 'marketing',
  },
  {
    id: 'send_service',
    lane: 6,
    cy: 190,
    title: ['Odeslání', 'servis'],
    sub: 'přímo z hubu (HTML)',
    icon: 'send',
    kind: 'service',
  },
  {
    id: 'send_marketing',
    lane: 6,
    cy: 350,
    title: ['Odeslání', 'marketing'],
    sub: 'šablona ve SmartEmailingu',
    icon: 'send',
    kind: 'marketing',
  },
  {
    id: 'se',
    lane: 7,
    cy: 270,
    title: ['SmartEmailing', ''],
    sub: 'doručení e-mailu',
    icon: 'mail',
    kind: 'all',
  },
  {
    id: 'react',
    lane: 7,
    cy: 430,
    title: ['Reakce', ''],
    sub: 'polling stavů a kliků',
    icon: 'up',
    kind: 'all',
  },
];

export const EDGES: EdgeDef[] = [
  { id: 'e1', from: 'src_eshop', to: 'ingest', kind: 'all' },
  { id: 'e2', from: 'src_web', to: 'ingest', kind: 'all' },
  { id: 'e3', from: 'src_feed', to: 'ingest', kind: 'all' },
  { id: 'e4', from: 'src_se', to: 'ingest', kind: 'all' },
  { id: 'e5', from: 'ingest', to: 'journeys', kind: 'all' },
  { id: 'e6', from: 'journeys', to: 'planner', kind: 'all' },
  { id: 'e7', from: 'planner', to: 'decisions', kind: 'all' },
  { id: 'e8', from: 'decisions', to: 'tpl_service', kind: 'service' },
  { id: 'e9', from: 'decisions', to: 'tpl_marketing', kind: 'marketing' },
  { id: 'e10', from: 'tpl_service', to: 'send_service', kind: 'service' },
  { id: 'e11', from: 'tpl_marketing', to: 'send_marketing', kind: 'marketing' },
  { id: 'e12', from: 'send_service', to: 'se', kind: 'service' },
  { id: 'e13', from: 'send_marketing', to: 'se', kind: 'marketing' },
  { id: 'e14', from: 'se', to: 'react', kind: 'all', soon: true },
  {
    id: 'e15',
    from: 'react',
    to: 'ingest',
    kind: 'all',
    soon: true,
    back: true,
    label: 'zpětná vazba: kontakty, segmenty, aktivita',
  },
];

export const nodeById = (id: string): NodeDef => NODES.find((n) => n.id === id) as NodeDef;
export const nodeX = (n: NodeDef): number => MARGIN + n.lane * PITCH;

/** Uzly a hrany, které se mají zvýraznit; zbytek se ztlumí. */
export interface Active {
  nodes: Set<string>;
  edges: Set<string>;
}

export function neighbourhood(ids: string[]): Active {
  const nodes = new Set(ids);
  const edges = new Set<string>();
  for (const e of EDGES) {
    if (ids.includes(e.from) || ids.includes(e.to)) {
      edges.add(e.id);
      nodes.add(e.from);
      nodes.add(e.to);
    }
  }
  return { nodes, edges };
}

export function kindActive(kind: Kind): Active | null {
  if (kind === 'all') return null;
  const ok = (k: Kind): boolean => k === 'all' || k === kind;
  return {
    nodes: new Set(NODES.filter((n) => ok(n.kind)).map((n) => n.id)),
    edges: new Set(EDGES.filter((e) => ok(e.kind)).map((e) => e.id)),
  };
}

const RACE_ANCHOR = /^race_(start|end)$/;
const anchorsOf = (rule: Rule): string[] =>
  Array.isArray(rule.anchor) ? rule.anchor : rule.anchor ? [rule.anchor] : [];

/** Cesta jedné zprávy diagramem. Zprávy posílané e-shopem končí u cest — hub je jen eviduje. */
export function tracePath(rules: Rule[]): { active: Active; ends: boolean } {
  const first = rules[0];
  if (!first) return { active: { nodes: new Set(), edges: new Set() }, ends: false };
  const anchors = rules.flatMap(anchorsOf);
  const ids = ['src_eshop', 'ingest', 'journeys'];
  if (anchors.some((a) => RACE_ANCHOR.test(a))) ids.push('src_feed');
  const hub = rules.some((r) => r.sender === 'hub');
  if (hub) {
    ids.push('planner', 'decisions');
    const kinds = new Set(rules.map((r) => r.channel));
    for (const k of kinds) ids.push(`tpl_${k}`, `send_${k}`);
    ids.push('se', 'react');
  }
  const set = new Set(ids);
  const edges = new Set(EDGES.filter((e) => set.has(e.from) && set.has(e.to)).map((e) => e.id));
  // Zdroje mimo skutečné spouštěče nesvítí; hrana e15 (zpětná vazba) patří k cestě.
  return { active: { nodes: set, edges }, ends: !hub };
}

/* -------------------------------------------------------------- čísla --- */

const sum = (rows: CodeStat[], pick: (r: CodeStat) => number): number =>
  rows.reduce((s, r) => s + pick(r), 0);
const inKind = (data: FlowData, kind: Kind): CodeStat[] =>
  kind === 'all' ? data.byCode : data.byCode.filter((c) => c.channel === kind);

export function outcomesFor(data: FlowData, kind: Kind): Record<string, number> {
  if (kind === 'all') return data.decisions.outcomes;
  const rows = inKind(data, kind);
  return {
    sent: sum(rows, (r) => r.sent),
    held: sum(rows, (r) => r.held),
    deferred: sum(rows, (r) => r.deferred),
    suppressed: sum(rows, (r) => r.suppressed),
    merged: sum(rows, (r) => r.merged),
    expired: sum(rows, (r) => r.expired),
  };
}

export function reasonsFor(
  data: FlowData,
  kind: Kind,
  code?: string,
): Array<{ outcome: string; reason: string; n: number }> {
  if (code) return data.byCode.find((c) => c.code === code)?.reasons ?? [];
  if (kind === 'all') return data.decisions.topReasons;
  const m = new Map<string, { outcome: string; reason: string; n: number }>();
  for (const c of inKind(data, kind)) {
    for (const r of c.reasons) {
      const key = `${r.outcome}/${r.reason}`;
      m.set(key, { ...r, n: (m.get(key)?.n ?? 0) + r.n });
    }
  }
  return [...m.values()].sort((a, b) => b.n - a.n).slice(0, 8);
}

export interface Metric {
  value: number | null;
  caption: string;
}

/** Hlavní číslo uzlu a popisek (po filtru servis / marketing přepočtené z kódů). */
export function metric(id: string, data: FlowData, kind: Kind): Metric {
  const rows = inKind(data, kind);
  const d = data.delivery;
  const dk = (k: 'service' | 'marketing'): number => (kind === 'all' || kind === k ? d[k].sent : 0);
  switch (id) {
    case 'src_eshop':
      return {
        value: data.ingest.bySource.filter((s) => s.source !== 'feed').reduce((s, r) => s + r.n, 0),
        caption: 'událostí',
      };
    case 'src_web':
      return { value: data.ingest.webEvents, caption: 'událostí' };
    case 'src_feed':
      return { value: data.journeys.races, caption: 'závodů' };
    case 'src_se':
      return { value: data.reactions.providerEvents, caption: 'událostí' };
    case 'ingest':
      return { value: data.ingest.received, caption: 'přijatých událostí' };
    case 'journeys':
      return { value: data.journeys.tripsActive, caption: 'aktivních cest' };
    case 'planner':
      return kind === 'all'
        ? { value: data.planner.created, caption: 'nových kandidátů' }
        : { value: sum(rows, (r) => r.pending), caption: 'čeká na řadě' };
    case 'decisions': {
      const o = outcomesFor(data, kind);
      return {
        value: Object.values(o).reduce((s, v) => s + v, 0),
        caption: 'rozhodnutí',
      };
    }
    case 'tpl_service':
      return { value: data.templates.published, caption: 'publikovaných šablon' };
    case 'tpl_marketing':
      return { value: data.templates.synced, caption: 'nahráno do SE' };
    case 'send_service':
      return { value: d.service.sent, caption: 'odesláno' };
    case 'send_marketing':
      return { value: d.marketing.sent, caption: 'odesláno' };
    case 'se':
      return { value: dk('service') + dk('marketing'), caption: 'předáno' };
    case 'react': {
      const pick = (r: Reaction): number => r.delivered;
      const total =
        (kind === 'marketing' ? 0 : pick(d.service)) + (kind === 'service' ? 0 : pick(d.marketing));
      return { value: total, caption: 'doručeno' };
    }
    default:
      return { value: null, caption: '' };
  }
}

/** Objem hrany pro tloušťku čáry. */
export function edgeVolume(e: EdgeDef, data: FlowData): number {
  const d = data.delivery;
  const react = (r: Reaction): number => r.opened + r.clicked + r.unsubscribed + r.bounced;
  switch (e.id) {
    case 'e1':
      return nodeVolume('src_eshop', data);
    case 'e2':
      return data.ingest.webEvents;
    case 'e3':
      return data.journeys.races;
    case 'e4':
      return data.reactions.providerEvents;
    case 'e5':
      return data.ingest.applied;
    case 'e6':
      return data.journeys.tripsActive;
    case 'e7':
      return data.decisions.total;
    case 'e8':
    case 'e10':
    case 'e12':
      return d.service.sent;
    case 'e9':
    case 'e11':
    case 'e13':
      return d.marketing.sent;
    case 'e14':
      return d.service.delivered + d.marketing.delivered;
    case 'e15':
      return react(d.service) + react(d.marketing) + data.reactions.providerEvents;
    default:
      return 0;
  }
}
const nodeVolume = (id: string, data: FlowData): number => metric(id, data, 'all').value ?? 0;

export function lastAtOf(id: string, data: FlowData): string | null {
  switch (id) {
    case 'src_eshop':
    case 'ingest':
      return data.ingest.lastAt;
    case 'src_web':
      return data.ingest.webLastAt;
    case 'src_feed':
    case 'journeys':
      return data.journeys.lastAt;
    case 'src_se':
    case 'react':
      return data.reactions.lastAt;
    case 'planner':
      return data.planner.lastAt;
    case 'decisions':
      return data.decisions.lastAt;
    case 'tpl_service':
    case 'tpl_marketing':
      return data.templates.lastAt;
    case 'send_service':
      return data.delivery.service.lastAt;
    case 'send_marketing':
      return data.delivery.marketing.lastAt;
    case 'se':
      return (
        [data.delivery.service.lastAt, data.delivery.marketing.lastAt]
          .filter(Boolean)
          .sort()
          .pop() ?? null
      );
    default:
      return null;
  }
}

/* ------------------------------------------------------- texty uzlů --- */

export interface NodeLink {
  text: string;
  tab?: CommTab;
  screen?: Screen;
}

export interface NodeInfo {
  title: string;
  what: string;
  why: string;
  rules: (s: FlowData['settings']) => string[];
  facts: (d: FlowData, kind: Kind) => Array<[string, string | number]>;
  links: NodeLink[];
}

const n = (v: number): string => new Intl.NumberFormat('cs-CZ').format(v);
const win = (w: { fromHour: number; toHour: number }): string => `${w.fromHour}–${w.toHour} h`;

export const NODE_INFO: Record<string, NodeInfo> = {
  src_eshop: {
    title: 'E-shop TicketsGP / Lights Out',
    what: 'E-shop posílá hubu události: zaplacené objednávky, změny vstupenek, dárky, Flexi a změny závodů. Každá událost má vlastní event_id a podpis.',
    why: 'Hub si nic nevymýšlí — žije jen z událostí. Bez nich neví, co zákazník koupil, a nemá co plánovat.',
    rules: () => [
      'Událost bez platného podpisu (HMAC) se odmítne.',
      'Stejné event_id se zpracuje jen jednou.',
      'Zprávy, které posílá e-shop sám (např. S01 Potvrzení objednávky), hub jen eviduje.',
    ],
    facts: (d) => [
      ['Události z e-shopu', n(metric('src_eshop', d, 'all').value ?? 0)],
      ...d.ingest.bySource
        .slice(0, 5)
        .map((s): [string, number] => [`${s.source} · ${s.type}`, s.n]),
    ],
    links: [{ text: 'Kvalita dat a napojení', screen: 'data_quality' }],
  },
  src_web: {
    title: 'Web snippet — události z webu',
    what: 'Skript na webu posílá, co návštěvníci dělají (například zobrazení závodu). Událost se spojí s kontaktem, jakmile ho hub umí identifikovat.',
    why: 'Z chování na webu se skládají segmenty a míra aktivity kontaktu.',
    rules: () => ['Anonymní události se uchovají a po identifikaci kontaktu se k němu přiřadí.'],
    facts: (d) => [['Událostí na webu', n(d.ingest.webEvents)]],
    links: [
      { text: 'Weby', screen: 'data_quality' },
      { text: 'Segmenty', tab: 'editor' },
    ],
  },
  src_feed: {
    title: 'Feed závodů',
    what: 'Kalendář závodů: termíny, stav prodeje, Flexi a race pack. Změna termínu se v katalogu promítne do zpráv S03 a S04.',
    why: 'Všechny časy zpráv se odvozují od začátku závodu — změna termínu musí přeplánovat všechno, co je s ním svázané.',
    rules: () => [
      'Zpráva o změně termínu se pošle jen poslední verzi termínu; starší verze propadnou (superseded).',
    ],
    facts: (d) => [['Závodů v kalendáři', n(d.journeys.races)]],
    links: [{ text: 'Závody', screen: 'races' }],
  },
  src_se: {
    title: 'SmartEmailing — reakce (webhook)',
    what: 'SmartEmailing bude hubu hlásit doručení, otevření, kliknutí, odhlášení a odskoky.',
    why: 'Reakce se vrací do kontaktů a segmentů a ovlivňují, komu a co se pošle příště.',
    rules: () => [
      'Připravuje se (fáze 2). Do té doby se stavy zjišťují jen z toho, co hub sám ví.',
    ],
    facts: (d) => [['Přijatých událostí od SE', n(d.reactions.providerEvents)]],
    links: [{ text: 'Kvalita dat a napojení', screen: 'data_quality' }],
  },
  ingest: {
    title: 'Hub: příjem událostí',
    what: 'Ověří podpis, uloží každou událost právě jednou (podle event_id), založí nebo aktualizuje kontakt, souhlasy a objednávku.',
    why: 'Idempotence: stejná událost přijatá dvakrát nesmí způsobit dvojí zpracování ani dvojí e-mail.',
    rules: (s) => [
      'Podpis (HMAC) se ověřuje u každé události.',
      'Duplicitní event_id se zahodí; zastaralá verze entity se označí „stale“.',
      `Marketingový souhlas se eviduje zvlášť pro každého prodejce.`,
      `Kontrolní skupina: ${s.holdoutPercent} % kontaktů marketing nedostane.`,
    ],
    facts: (d) => [
      ['Přijato', n(d.ingest.received)],
      ['Zpracováno', n(d.ingest.applied)],
      ['Zastaralé verze', n(d.ingest.stale)],
      ['Čeká na zpracování', n(d.ingest.pending)],
      ['Selhalo', n(d.ingest.failed)],
      ['Duplicity', 'zahazují se bez počítání'],
      ['Kontaktů', n(d.contacts.total)],
      ['S marketingovým souhlasem', n(d.contacts.withConsent)],
    ],
    links: [
      { text: 'Zákazníci', screen: 'customers' },
      { text: 'Kvalita dat a napojení', screen: 'data_quality' },
      { text: 'Výjimky', screen: 'exceptions' },
    ],
  },
  journeys: {
    title: 'Cesty zákazníků a kalendář',
    what: 'Z objednávek a závodů vzniká cesta zákazníka: ke kterému závodu jede, v jakém je stavu (čeká na termín, vstupenky se chystají, týden závodu…) a s jakým předstihem nakoupil.',
    why: 'Plánovač počítá termíny zpráv právě z cesty. Zavřená cesta zprávy neposílá (kromě pravidel s allowed_on_closed).',
    rules: (s) => [
      'Cesta je jedna pro dvojici kontakt + závod + prodejce.',
      `Okno závodu: marketing se nepošle ${s.raceWindow.before_days} dní před závodem a ${s.raceWindow.after_days} den po něm.`,
    ],
    facts: (d) => [
      ['Aktivních cest', n(d.journeys.tripsActive)],
      ['Nových cest v období', n(d.journeys.tripsCreated)],
      ['Závodů', n(d.journeys.races)],
    ],
    links: [
      { text: 'Cesty', screen: 'trips' },
      { text: 'Závody', screen: 'races' },
    ],
  },
  planner: {
    title: 'Plánovač',
    what: 'Pro každou cestu projde katalog a vytvoří kandidáty zpráv: co se má poslat, kdy (kotva + posun) a do kdy to ještě dává smysl (platnost).',
    why: 'Plánovač nepošle nic, co už nemá smysl: zpětné termíny přeskočí a pravidla s minimálním předstihem nepoužije u cest, které je nesplňují.',
    rules: () => [
      'Termín = kotva (např. začátek závodu) + posun z katalogu.',
      'Nikdy zpětně: termín před nákupem se přeskočí, pokud pravidlo nemá catch_up.',
      'min_lead_days: pravidlo se nepoužije u cest s kratším předstihem.',
      'Každý kandidát má jedinečný klíč výskytu — nevznikne dvakrát.',
    ],
    facts: (d, kind) => {
      const rows = inKind(d, kind);
      return [
        ...(kind === 'all'
          ? ([['Nových kandidátů v období', n(d.planner.created)]] as Array<[string, string]>)
          : []),
        ['Otevřených', n(kind === 'all' ? d.planner.open : sum(rows, (r) => r.pending))],
        ['Podržených (celkem)', n(d.planner.held)],
        ['Odložených (celkem)', n(d.planner.deferred)],
      ];
    },
    links: [
      { text: 'Tok zpráv (pravidla)', tab: 'flow' },
      { text: 'Plán odesílání', tab: 'plan' },
    ],
  },
  decisions: {
    title: 'Rozhodnutí',
    what: 'Každý kandidát projde kontrolami: vypínač, stav cesty, podmínky pravidla, dřívější trvalé odskočení adresy, souhlas, kontrolní skupina, okno závodu, frekvenční limit, sloučení a časové okno.',
    why: 'Tady se rozhoduje o důvěře zákazníků. Každá neodeslaná zpráva má zapsaný výsledek a důvod — tichý konec je chyba.',
    rules: (s) => [
      `Standardní okno ${win(s.windows.standard)}, důležité ${win(s.windows.critical)}, okamžité kdykoli (v čase příjemce).`,
      `Frekvenční limit marketingu: ${s.frequencyCapDays} dní (kromě pravidel mimo limit).`,
      `Slučování zpráv ze stejné skupiny do ${s.mergeWindowHours} h.`,
      `Marketing se odkládá po kontaktu s podporou na ${s.supportPauseDays} dní.`,
      'Marketing vyžaduje souhlas pro daného prodejce a kontakt nesmí být v kontrolní skupině.',
      'Režim odesílání nanečisto: hub vše spočítá, ale nic neodešle.',
    ],
    facts: (d, kind) => {
      const o = outcomesFor(d, kind);
      return [
        ['Odesláno', n(o.sent ?? 0)],
        ['Sloučeno', n(o.merged ?? 0)],
        ['Podrženo', n(o.held ?? 0)],
        ['Odloženo', n(o.deferred ?? 0)],
        ['Potlačeno', n(o.suppressed ?? 0)],
        ['Propadlo', n(o.expired ?? 0)],
      ];
    },
    links: [
      { text: 'Přehled zpráv a vypínače', tab: 'catalogue' },
      { text: 'Výjimky', screen: 'exceptions' },
      { text: 'Nastavení', screen: 'settings' },
    ],
  },
  tpl_service: {
    title: 'Šablona — servis',
    what: 'Obsah e-mailu se skládá z bloků a převádí do MJML. U servisních zpráv hub vyrenderuje celé HTML a pošle ho tak, jak je.',
    why: 'Odeslané HTML se ukládá s otiskem, takže jde vždy dohledat, co zákazník opravdu dostal.',
    rules: () => [
      'Šablona se vybírá podle kódu zprávy, prodejce a jazyka.',
      'Chybí-li publikovaná šablona, zpráva se podrží (template_missing).',
    ],
    facts: (d) => [
      ['Šablon celkem', n(d.templates.total)],
      ['Publikovaných', n(d.templates.published)],
    ],
    links: [
      { text: 'Editor šablon', tab: 'editor' },
      { text: 'Kanály', tab: 'channels' },
    ],
  },
  tpl_marketing: {
    title: 'Šablona — marketing',
    what: 'Marketingová šablona se nahraje do SmartEmailingu a při odeslání se do ní jen dosadí hodnoty (tokeny replace).',
    why: 'Marketingové e-maily odcházejí ze SmartEmailingu, takže šablona musí být nahraná, jinak se zpráva nepošle.',
    rules: () => [
      'Nenahraná šablona: zpráva se podrží (template_not_synced).',
      'Hodnota proměnné nesmí být prázdná ani větší než 64 kB.',
    ],
    facts: (d) => [
      ['Publikovaných šablon', n(d.templates.published)],
      ['Nahráno do SmartEmailingu', n(d.templates.synced)],
    ],
    links: [
      { text: 'Mapování v SmartEmailingu', tab: 'templates' },
      { text: 'Kanály', tab: 'channels' },
    ],
  },
  send_service: {
    title: 'Odeslání — servis',
    what: 'Servisní zprávy (potvrzení, vstupenky, změny termínu) odcházejí přes transakční rozhraní SmartEmailingu jako hotové HTML z hubu.',
    why: 'Transakční cesta je rychlá a nezávisí na marketingovém souhlasu.',
    rules: (s) => [
      `Režim odesílání: ${s.sendMode === 'live' ? 'ostrý' : s.sendMode === 'dry_run' ? 'nanečisto' : 'zkušební adresy'}.`,
      'Každá zpráva má jedinečný klíč položky — druhé odeslání se zamkne.',
    ],
    facts: (d) => reactFacts(d.delivery.service),
    links: [
      { text: 'Kanály', tab: 'channels' },
      { text: 'Nastavení', screen: 'settings' },
    ],
  },
  send_marketing: {
    title: 'Odeslání — marketing',
    what: 'Marketing odchází přes SmartEmailing s šablonou, kterou hub předem nahrál, a hub do ní při odeslání dosadí data.',
    why: 'Hub zapisuje do SmartEmailingu jen povolená pole (hub_*, language) a nikdy prázdné hodnoty.',
    rules: (s) => [
      `Režim odesílání: ${s.sendMode === 'live' ? 'ostrý' : s.sendMode === 'dry_run' ? 'nanečisto' : 'zkušební adresy'}.`,
      `Způsob předání marketingu: ${s.marketingDelivery}.`,
      'Zpráva odejde jen když smí kód odejít (vypínač) a je zapnutý globální vypínač.',
    ],
    facts: (d) => reactFacts(d.delivery.marketing),
    links: [
      { text: 'Kanály', tab: 'channels' },
      { text: 'Přehled zpráv a vypínače', tab: 'catalogue' },
    ],
  },
  se: {
    title: 'SmartEmailing',
    what: 'Externí služba, která e-mail skutečně doručí příjemci. Hub jí předá zprávu a (později) čte, co se s ní stalo.',
    why: 'SmartEmailing drží kontakty a odhlášení. Hub jen doplňuje pole hub_* a nesahá na cizí data.',
    rules: () => ['Nikdy se nezapisuje prázdná hodnota ani pole „Ecomail - tags“.'],
    facts: (d) => [
      ['Předáno (servis)', n(d.delivery.service.sent)],
      ['Předáno (marketing)', n(d.delivery.marketing.sent)],
    ],
    links: [{ text: 'Nastavení — napojení', screen: 'settings' }],
  },
  react: {
    title: 'Reakce a zpětná vazba',
    what: 'Doručeno, otevřeno, kliknuto, odhlášeno, odskočeno. Stavy se vracejí do zpráv, kontaktů, segmentů a míry aktivity.',
    why: 'Uzavírá smyčku: odskočená adresa se přestane používat, odhlášení se respektuje a aktivní kontakt dostane jiný obsah.',
    rules: () => [
      'Hub každých 15 minut stahuje reakce ze SmartEmailingu (polling), případně je přijme webhookem. Každá reakce se zapíše do knihy, spáruje se se zprávou a promítne do kontaktu.',
    ],
    facts: (d) => {
      const a = d.delivery.service;
      const b = d.delivery.marketing;
      return [
        ['Doručeno', n(a.delivered + b.delivered)],
        ['Otevřeno', n(a.opened + b.opened)],
        ['Kliknuto', n(a.clicked + b.clicked)],
        ['Odhlášeno', n(a.unsubscribed + b.unsubscribed)],
        ['Odskočeno', n(a.bounced + b.bounced)],
        [
          'Poslední běh pollingu',
          d.reactions.poll.lastRunAt
            ? d.reactions.poll.status === 'ok'
              ? 'v pořádku'
              : 'selhal'
            : 'ještě neběžel',
        ],
      ];
    },
    links: [
      { text: 'Úlohy (běh pollingu reakcí)', screen: 'jobs' },
      { text: 'Přehled zpráv (kliky, odhlášení)', tab: 'catalogue' },
      { text: 'Přehled', screen: 'overview' },
    ],
  },
};

function reactFacts(r: Reaction): Array<[string, string | number]> {
  return [
    ['Odesláno', n(r.sent)],
    ['z toho nanečisto', n(r.dryRun)],
    ['Doručeno', n(r.delivered)],
    ['Otevřeno', n(r.opened)],
    ['Kliknuto', n(r.clicked)],
    ['Odhlášeno', n(r.unsubscribed)],
    ['Odskočeno', n(r.bounced)],
  ];
}

/* --------------------------------------------------- pravidlo zprávy --- */

const ANCHOR_LABEL: Record<string, string> = {
  order_paid: 'zaplacení objednávky',
  race_start: 'začátku závodu',
  race_end: 'konce závodu',
  flexi_offer_due: 'termínu nabídky Flexi',
  gift_deliver_at: 'předání dárku',
  gift_expires_at: 'konce platnosti dárku',
  review_due: 'termínu hodnocení',
};

function offsetText(offset: string | number | undefined): { sign: -1 | 0 | 1; text: string } {
  if (offset === undefined || offset === '0' || offset === 0) return { sign: 0, text: '' };
  const m = /^([+-]?)(\d+)\s*([dh])$/.exec(String(offset).trim());
  if (!m) return { sign: 0, text: String(offset) };
  const value = Number(m[2]);
  const unit =
    m[3] === 'd'
      ? value === 1
        ? 'den'
        : value < 5
          ? 'dny'
          : 'dní'
      : value === 1
        ? 'hodina'
        : value < 5
          ? 'hodiny'
          : 'hodin';
  return { sign: m[1] === '-' ? -1 : 1, text: `${value} ${unit}` };
}

function durationText(v: string): string {
  const o = offsetText(v);
  return o.text || v;
}

export function triggerText(rule: Rule): string {
  if (rule.sender === 'eshop' && !rule.anchor) return 'Zprávu posílá e-shop; hub ji jen eviduje.';
  const anchors = anchorsOf(rule).map((a) =>
    a.startsWith('event:') ? `událost ${a.slice(6)}` : (ANCHOR_LABEL[a] ?? a),
  );
  const off = offsetText(rule.offset);
  const base = anchors.join(' nebo ');
  if (off.sign === 0)
    return anchors[0]?.startsWith('událost') ? `Hned po události: ${base}` : `V den ${base}`;
  const rel = off.sign < 0 ? `${off.text} před` : `${off.text} po`;
  return anchors[0]?.startsWith('událost')
    ? `${rel} události ${base.replace(/událost /g, '')}`
    : `${rel} ${base}`;
}

/** Konkrétní pravidlo kódu zprávy, přeložené z katalogu do češtiny. */
export function ruleFacts(rule: Rule, s: FlowData['settings']): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  out.push([
    'Druh',
    `${label(CHANNEL_LABEL, rule.channel)} · posílá ${label(SENDER_LABEL, rule.sender).toLowerCase()}`,
  ]);
  out.push(['Spouštěč', triggerText(rule)]);
  if (rule.valid_for)
    out.push([
      'Platnost',
      `smí odejít nejpozději ${durationText(rule.valid_for)} po termínu, pak propadne`,
    ]);
  if (rule.window) {
    const w =
      rule.window === 'standard'
        ? s.windows.standard
        : rule.window === 'critical'
          ? s.windows.critical
          : null;
    out.push([
      'Časové okno',
      w ? `${rule.window === 'standard' ? 'Standardní' : 'Důležité'} (${win(w)})` : 'Kdykoli',
    ]);
  }
  out.push([
    'Souhlas',
    rule.channel === 'marketing'
      ? 'vyžaduje marketingový souhlas daného prodejce'
      : 'nevyžaduje se (servisní zpráva)',
  ]);
  if (rule.requires_date && rule.requires_date !== 'any')
    out.push([
      'Termín závodu',
      rule.requires_date === 'confirmed' ? 'jen potvrzený' : 'známý (předběžný nebo potvrzený)',
    ]);
  if (rule.min_lead_days)
    out.push(['Minimální předstih', `jen když cesta má aspoň ${rule.min_lead_days} dní do závodu`]);
  const cond = rule.conditions ?? [];
  if (cond.length > 0)
    out.push(['Nepošle se, když', cond.map((c) => label(REASON_LABEL, c)).join('; ')]);
  if (rule.merge_group)
    out.push([
      'Sloučení',
      `skupina ${rule.merge_group}: zprávy do ${s.mergeWindowHours} h se pošlou jako jedna`,
    ]);
  if (rule.channel === 'marketing')
    out.push([
      'Frekvence',
      rule.pacing_exempt
        ? 'mimo frekvenční limit a okno závodu'
        : `frekvenční limit ${s.frequencyCapDays} dní, okno závodu, pauza ${s.supportPauseDays} dní po podpoře`,
    ]);
  if (rule.priority !== undefined)
    out.push(['Priorita', `${rule.priority} (nižší číslo vyhrává při souběhu)`]);
  if (rule.catch_up) out.push(['Dobíhání', 'smí odejít hned po nákupu, i když termín už byl']);
  if (rule.allowed_on_closed) out.push(['Zavřená cesta', 'smí odejít i na ukončenou cestu']);
  if (rule.template_key) out.push(['Šablona', rule.template_key]);
  return out;
}

export interface WalkStep {
  title: string;
  nodes: string[];
  text: string;
}

/** Příklad „koupil lístek N dní před závodem“ — texty z reálného náhledu cesty a nastavení. */
export function walkthrough(
  leadDays: number,
  steps: JourneyStep[],
  s: FlowData['settings'],
  rules: Rule[],
): WalkStep[] {
  const hub = steps.filter((x) => x.sender === 'hub');
  const kept = hub.filter((x) => x.included);
  const lead = (code: string): number | undefined =>
    rules.find((r) => r.code === code && r.min_lead_days)?.min_lead_days;
  const group = (reason: string): JourneyStep[] =>
    hub.filter((x) => !x.included && x.reason === reason);
  const codes = (rows: JourneyStep[]): string => [...new Set(rows.map((r) => r.code))].join(', ');
  const late = group('too_late');
  const minLead = group('min_lead');
  const needsDate = group('needs_date');
  const dropped = [
    late.length
      ? `termín by vyšel před nákupem, plánovač nikdy neplánuje zpětně (${codes(late)})`
      : '',
    minLead.length
      ? `pravidlo vyžaduje větší předstih než ${leadDays} dny (${[...new Set(minLead.map((r) => r.code))].map((c) => `${c}: min. ${lead(c) ?? '?'} dní`).join(', ')})`
      : '',
    needsDate.length ? `čeká na známý termín závodu (${codes(needsDate)})` : '',
  ].filter(Boolean);
  return [
    {
      title: 'Zákazník koupí lístek',
      nodes: ['src_eshop'],
      text: `Zákazník zaplatí objednávku na e-shopu ${leadDays} dny před závodem. E-shop pošle hubu událost s podpisem a jedinečným event_id.`,
    },
    {
      title: 'Hub událost přijme',
      nodes: ['ingest'],
      text: 'Hub ověří podpis, událost uloží právě jednou (podle event_id) a založí nebo aktualizuje kontakt, jeho souhlasy a objednávku.',
    },
    {
      title: 'Vznikne cesta',
      nodes: ['journeys'],
      text: `Z objednávky vznikne cesta zákazníka k závodu. Její předstih je ${leadDays} dny — právě ten rozhoduje, které zprávy ještě dávají smysl.`,
    },
    {
      title: 'Plánovač vybírá zprávy',
      nodes: ['planner'],
      text: `Plánovač zprávy, které už by nedávaly smysl, vůbec nenaplánuje. Zůstává ${new Set(kept.map((r) => r.code)).size} zpráv (${codes(kept)}). Vynechává: ${dropped.length ? dropped.join('; ') : 'nic'}.`,
    },
    {
      title: 'Rozhodnutí o každé zprávě',
      nodes: ['decisions'],
      text: `Každý kandidát projde kontrolami: vypínač, stav cesty, podmínky pravidla, dřívější odskočení adresy, souhlas, kontrolní skupina (${s.holdoutPercent} %), frekvenční limit (${s.frequencyCapDays} dní), sloučení (${s.mergeWindowHours} h) a časové okno ${win(s.windows.standard)} v čase příjemce. Co neodejde, má zapsaný důvod.`,
    },
    {
      title: 'Šablona',
      nodes: ['tpl_service', 'tpl_marketing'],
      text: 'Servisní zpráva se vyrenderuje z bloků do plného HTML. Marketing použije šablonu nahranou ve SmartEmailingu a dosadí do ní hodnoty.',
    },
    {
      title: 'Odeslání',
      nodes: ['send_service', 'send_marketing'],
      text: `Servis jde přímo z hubu, marketing přes šablonu ve SmartEmailingu. Režim: ${s.sendMode === 'dry_run' ? 'nanečisto (nic neodchází ven)' : s.sendMode === 'live' ? 'ostrý' : 'zkušební adresy'}.`,
    },
    {
      title: 'SmartEmailing doručí',
      nodes: ['se'],
      text: 'SmartEmailing e-mail doručí příjemci. Hub k němu eviduje stav zprávy.',
    },
    {
      title: 'Reakce se vrací',
      nodes: ['react', 'ingest'],
      text: 'Doručení, otevření, kliknutí nebo odhlášení se vrátí do kontaktu, segmentů a aktivity. Podle toho se rozhoduje o další komunikaci.',
    },
  ];
}

export const stripFocus: Record<string, string[]> = {
  Událost: ['ingest'],
  Plán: ['planner'],
  Rozhodnutí: ['decisions'],
  Šablona: ['tpl_service', 'tpl_marketing'],
  Odeslání: ['send_service', 'send_marketing'],
  Reakce: ['react'],
};
